/**
 * Validates and sanitizes redirect URLs to prevent open redirect vulnerabilities.
 *
 * This utility ensures that redirect URLs are same-origin relative paths,
 * preventing attackers from using redirect parameters to bounce users to
 * external malicious domains after authentication.
 *
 * @example
 * ```ts
 * // Valid redirects (same-origin relative paths)
 * validateRedirect('/dashboard') // '/dashboard'
 * validateRedirect('/jobs/123') // '/jobs/123'
 * validateRedirect('/jobs?view=calendar') // '/jobs?view=calendar'
 * validateRedirect('') // '/'
 * validateRedirect(null) // '/'
 *
 * // Invalid redirects (external URLs or protocol-relative URLs)
 * validateRedirect('https://evil.example') // '/'
 * validateRedirect('//evil.example') // '/'
 * validateRedirect('javascript:alert(1)') // '/'
 * ```
 */

/**
 * Validates that a redirect URL is a safe same-origin relative path.
 * Returns the validated path if safe, otherwise returns the fallback path.
 *
 * @param redirectTo - The redirect URL to validate (can be null/undefined)
 * @param fallback - The fallback path to use if validation fails (default: '/')
 * @returns A safe redirect path
 */
export const validateRedirect = (
  redirectTo: string | null | undefined,
  fallback: string = '/'
): string => {
  // Handle null, undefined, or empty string
  if (
    !redirectTo ||
    typeof redirectTo !== 'string' ||
    redirectTo.trim() === ''
  ) {
    return fallback;
  }

  const trimmedRedirect = redirectTo.trim();

  // Block protocol-relative URLs (e.g., //evil.example)
  if (trimmedRedirect.startsWith('//')) {
    console.warn(
      `[Security] Blocked protocol-relative redirect: ${trimmedRedirect}`
    );
    return fallback;
  }

  // Block URLs with explicit protocols (e.g., https://, http://, javascript:, data:)
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmedRedirect)) {
    console.warn(
      `[Security] Blocked redirect with explicit protocol: ${trimmedRedirect}`
    );
    return fallback;
  }

  // Block URLs that contain backslashes (potential bypass attempts)
  if (trimmedRedirect.includes('\\')) {
    console.warn(
      `[Security] Blocked redirect with backslashes: ${trimmedRedirect}`
    );
    return fallback;
  }

  // Ensure the path starts with a forward slash
  // This guarantees it's a same-origin relative path
  if (!trimmedRedirect.startsWith('/')) {
    console.warn(
      `[Security] Blocked redirect not starting with '/': ${trimmedRedirect}`
    );
    return fallback;
  }

  // Additional validation: try to parse as URL with a base
  // This catches edge cases where the path might be malformed
  try {
    const url = new URL(trimmedRedirect, 'http://localhost');

    // Ensure the parsed URL's origin matches our base (same-origin check)
    if (url.origin !== 'http://localhost') {
      console.warn(
        `[Security] Blocked redirect with different origin: ${trimmedRedirect}`
      );
      return fallback;
    }

    // Return the pathname + search + hash (without the origin)
    return url.pathname + url.search + url.hash;
  } catch {
    // If URL parsing fails, it's likely malformed
    console.warn(
      `[Security] Blocked malformed redirect URL: ${trimmedRedirect}`
    );
    return fallback;
  }
};

/**
 * Validates redirect data stored in sessionStorage.
 * Expects the format: { pathname: string, t: number }
 *
 * @param maxAgeMs - Maximum age of the redirect in milliseconds (default: 15 minutes)
 * @returns A safe redirect path or undefined if validation fails or data is expired
 */
export const getValidatedRedirectFromStorage = (
  maxAgeMs: number = 15 * 60 * 1000 // 15 minutes
): string | undefined => {
  try {
    const redirectData = window.sessionStorage.getItem('redirectTo');
    if (!redirectData) {
      return undefined;
    }

    const parsed = JSON.parse(redirectData);

    // Validate structure
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !parsed.pathname ||
      !parsed.t
    ) {
      console.warn(
        '[Security] Invalid redirect data structure in sessionStorage'
      );
      window.sessionStorage.removeItem('redirectTo');
      return undefined;
    }

    // Check expiration
    const now = new Date().getTime();
    if (now - parsed.t > maxAgeMs) {
      console.warn('[Security] Expired redirect data in sessionStorage');
      window.sessionStorage.removeItem('redirectTo');
      return undefined;
    }

    // Validate the pathname
    const validatedPath = validateRedirect(parsed.pathname);

    // Only return if validation succeeded (not the fallback)
    if (validatedPath === '/' && parsed.pathname !== '/') {
      // Validation failed, the path was rejected
      window.sessionStorage.removeItem('redirectTo');
      return undefined;
    }

    return validatedPath;
  } catch (error) {
    console.warn(
      '[Security] Error reading redirect from sessionStorage:',
      error
    );
    try {
      window.sessionStorage.removeItem('redirectTo');
    } catch {
      // Ignore storage errors during cleanup
    }
    return undefined;
  }
};
